// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from "vue";
import Vuetify from "vuetify";
import "vuetify/dist/vuetify.min.css";
import VueTelInput from "vue-tel-input";
import "vue-tel-input/dist/vue-tel-input.css";
import appConfig from "./appconfig";

import App from "./App";
import * as VueGoogleMaps from "vue2-google-maps";
import createStore from "./store";
import i18nProvider from "./providers/localization/i18nProvider";
import AppDate from "../src/domains2/libraries/AppDate";

import * as actions from "./store/mutation-types";
import ServerLogProvider from "./providers/ServerLogProvider";
import mobileEvents from "./mobile/mobileEvents";
import globalFilters from "./globalFilters";
import {getBaseDomain, isObjectOrArray, subscribeToUniversalLinks, isDomainMatch} from "./libraries/utils";
import {ApmHelper} from "./domains2/libraries/ApmHelper";

import { Adjust, AdjustConfig, AdjustEnvironment } from "@awesome-cordova-plugins/adjust";
import { AdvertisingId } from "@capacitor-community/advertising-id";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import {ADMIN_REMOVE_JWT_TOKEN} from "./store/mutation-types";
import * as mutationTypes from "./store/mutation-types";
import * as actionTypes from "./store/mutation-types";
import mutationTypesTid6 from "./store/tenantSpecificModules/storeModulesTid6/mutationTypesTid6.js";

import gigyaSdkHelper from "./helper/gigyaSdkHelper";
import { FacebookLogin } from "@capacitor-community/facebook-login";

Vue.config.productionTip = false;
const INVALID_BASKET_STATE_ERRORS = {
	ConfigurationGroupNotFoundError: true,
	ProductIsUndefinedError: true
};

Vue.use(Vuetify);
Vue.use(VueTelInput);
Vue.use(VueGoogleMaps, {
	load: {
		key: "AIzaSyBXEKC2d_k2VIPjjWeOTTBe7Nj5rsEGp5E",
		libraries: "places" // necessary for places input
	}
});

// apply global filters
Object.keys(globalFilters).forEach(filterName => {
	Vue.filter(filterName, globalFilters[filterName]);
});

function safeStringify(obj) {
	try {
		return JSON.stringify(obj);
	} catch (e) {
		// eslint-disable-next-line no-console
		console.log("Could not stringify object");
		// eslint-disable-next-line no-console
		console.log(e);
		return obj + "";
	}
}

async function handleAndLogError(_err) {
	// check if main vue div failed loading
	if (document.getElementById("app").innerHTML.trim() === "") {
		if (window.CHANNEL_ALIAS === "kiosk") {
			// for kiosk reload automatically
			// eslint-disable-next-line no-console
			console.log("Vue app failed, reloading...");
			setTimeout(() => {
				window.location.reload();
			}, 5000);
		}

		document.getElementById("reloadAppWrapper").style.display = "block";
	}

	const err = isObjectOrArray(_err) ? _err : new Error(_err + "");
	if (err.isLogged) {
		return;
	}
	try {

		if (appConfig.isNonProdZone()) {
			err.friendlyMessage = `Der skete noget uventet (ERR-${appConfig.clientTraceId()}). Vi beklager.`;
		}
		if(ApmHelper.isEnabled()) {
			const {version: appVersion} = await appConfig.getAppInfo();
			ApmHelper.captureError(err, { appVersion });
		} else {
			ServerLogProvider.postErrorToServer(err);
		}
		// eslint-disable-next-line no-console
		console.log(safeStringify(err));
	} catch (err2) {
		// eslint-disable-next-line no-console
		console.log("Original error: ");
		// eslint-disable-next-line no-console
		console.log(safeStringify(err));
		// eslint-disable-next-line no-console
		console.log("Error happening during error handling: ");
		// eslint-disable-next-line no-console
		console.log(safeStringify(err2));
	}
	err.isLogged = true;
}

try {
	window.addEventListener("unhandledrejection", async err => {
		await handleAndLogError(err);
	});
} catch (e) {
	// eslint-disable-next-line no-console
	console.log("Could not setup global handler for unhandled rejections.");
	// eslint-disable-next-line no-console
	console.log(e);
}

const checkForExpiredProductSelections = (myStore) => {
	// find all expired productSelections
	const expiredSelections = myStore.getters.selections.filter(selection => {
		if (selection.expiration && selection.expiration.expirationTime < AppDate.realNowTimestamp()) {
			return true;
		}
		return false;
	});
	// if there are some add a global error
	if (expiredSelections.length > 0) {
		myStore.dispatch(actions.ADD_SELECTION_EXPIRED_MESSAGE);
	}
	// remove expired productSelections from basket
	expiredSelections.forEach(expiredSelection => {
		myStore.dispatch(actions.REMOVE_FROM_CART_WITHOUT_CHECKOUT_RESET, expiredSelection.productSelectionId);
	});
};

const preInitApp = async () => {
	await appConfig.cacheAppInfo();
};

const initApp = () => {
	const initialize = (myStore, router) => {
		// values from local storage
		const showLongKeys = localStorage.getItem("showLongTranslationKeys");
		const showShortKeys = localStorage.getItem("showShortTranslationKeys");
		// Filter for translating product catalog stuff with fallback to original value.
		const showStaticTranslationKeys = (translationKey,options) => {
			if (showLongKeys === "true") {
				return translationKey;
			}
			if(showShortKeys === "true") {
				return translationKey.substring(translationKey.lastIndexOf(".") + 1);
			}
			return i18nProvider.t(translationKey,options);
		};

		const showDynamicTranslationKeys = (defaultValue,translationKey) => {
			const isDynamicLocalizationEnabled = () => typeof myStore.getters.featureToggles.dynamicLocalization !== "undefined"
				? myStore.getters.featureToggles.dynamicLocalization
				: appConfig.tenantConfig.enableDynamicLocalization;

			if (showLongKeys === "true") {
				return translationKey;
			}

			if(showShortKeys === "true") {
				return translationKey.substring(translationKey.lastIndexOf(".") + 1);
			}

			if (!isDynamicLocalizationEnabled() || !i18nProvider.te(translationKey)) {
				return defaultValue;
			}
			return i18nProvider.t(translationKey);
		};

		Vue.filter("translate", showDynamicTranslationKeys);
		Vue.prototype.$translate = showDynamicTranslationKeys;
		Vue.prototype.$t = showStaticTranslationKeys;

		checkForExpiredProductSelections(myStore);

		Vue.config.errorHandler = function (_err, vueComponent, vueContextDescription) {
			const err = isObjectOrArray(_err) ? _err : new Error(_err + "");
			if (INVALID_BASKET_STATE_ERRORS[err.errorCode]) {
				if (myStore.getters.unavailableProductIds.length > 0) {
					return;
				}
				console.log(err); // eslint-disable-line no-console
				const msg = "Din kurv er blevet tømt, da vi fandt nogle produkter i din kurv, som ikke længere findes.";
				myStore.dispatch(actions.ADD_GLOBAL_ERROR, {friendlyMessage: msg});
				myStore.dispatch(actions.CART_EMPTY_ALL);
			} else {
				try {
					if (vueComponent && vueComponent.$options) {
						err.vueComponentName = vueComponent.$options._componentTag;
					}
					if (vueContextDescription) {
						err.vueContextDescription = vueContextDescription;
					}
				} catch (err2) {
					// eslint-disable-next-line no-console
					console.log("Error while adding vueContext");
				}
				// eslint-disable-next-line no-console
				console.log(err);
				handleAndLogError(err);
			}
		};

		if (appConfig.isBM100()) {
			subscribeToUniversalLinks(router.default, async (eventData, slug) => {
				// eslint-disable-next-line no-undef
				const tenantMappingData = TENANT_MAPPING_DATA;
				const url = new URL(eventData.url);
				const hostname = url.hostname;
				const tenantName = (() => {
					const tenantUrlMap = tenantMappingData.urlTenantMapping.find(item => {
						return hostname.indexOf(item.urlSubString) !== -1;
					});

					return tenantUrlMap ? tenantUrlMap.tenantName : undefined;
				})();

				const allTenantMap = Object.keys(tenantMappingData.tenantsData).reduce((carry, tid) => {
					return {
						...carry,
						[tid]: tenantMappingData.tenantsData[tid].map
					};
				}, {});

				const tenantMap = Object
					.entries(allTenantMap)
					.map(([tenantId, tenantMap]) => ([+tenantId, tenantMap]))
					.map(([tenantId, tenantValueMap]) => {
						return tenantValueMap.reduce((prev, curr) => ({...prev, [curr]: tenantId}), {});
					})
					.reduce((prev, curr) => ({...prev, ...curr}), {});

				if (tenantMap[tenantName]) {
					const toBeTenantId = parseInt(tenantMap[tenantName]);
					const currentTenantId = parseInt(appConfig.tenantId);
					if (toBeTenantId !== currentTenantId) {
						if (slug) {
							sessionStorage.setItem("slug", slug);
						}
						await SplashScreen.show({
							autoHide: false
						});
						if (appConfig.mobile.mobileApi().platformId === "android") {
							StatusBar.setOverlaysWebView({ overlay: false });
							StatusBar.setBackgroundColor({ color: "#000000" });
							await StatusBar.setStyle({ style: Style.Dark });
						}
						window.localStorage.setItem("tenant", toBeTenantId + "");
						window.location.reload();
					}
				}
			});

			document.addEventListener("deviceready", () => {
				const slugToPush = sessionStorage.getItem("slug");
				if (slugToPush) {
					setTimeout(() => {
						router.default.push({path: slugToPush}, () => {
							sessionStorage.removeItem("slug");
						});
					},0);
				}
			}, false);
		}

		if (
			appConfig.isBM6() && localStorage.getItem("openByMobileApp") === "true" &&
			["/payment/accept", "/shop-selection"].includes(window.location.pathname)
		) {
			const enableGigyaIntergrationNewApproach = myStore.getters.featureToggles.enableGigyaIntergrationNewApproach !== undefined
				? myStore.getters.featureToggles.enableGigyaIntergrationNewApproach : false;

			if (!enableGigyaIntergrationNewApproach) {
				let eventReceived = false;

				const timeoutId = setTimeout(() => {
					if (!eventReceived) {
						eventReceived = true;

						localStorage.setItem("gigyaDataReceived", true); // To stop the gigya getAccountInfo callback processing
						// If no data was received, emit an error event
						myStore.commit(actions.SET_GIGYA_ERROR, {
							isGigyaError: true,
							errorMessage: "GigyaData event not Received due to timeout 5 seconds"
						});
						myStore.commit(actions.SET_GIGYA_ACCOUNT_INFO, {});
					}
				}, 6000);

				window.addEventListener("gigyaDataReceived", (event) => {
					if (!eventReceived) {
						clearTimeout(timeoutId);
						eventReceived = true;
						if (event.detail.isGigyaError) {
							const errorMessage = event.detail.errorMessage || "Failed to load account info";
							const errorCode = event.detail.responseErrorCode || -1;

							myStore.commit(actions.SET_GIGYA_ERROR, {
								isGigyaError: true,
								errorMessage: `${errorMessage} - errorCode: ${errorCode}`
							});
							myStore.dispatch(actions.SET_GIGYA_ACCOUNT_INFO, {});
						} else {
							myStore.dispatch(actions.SET_GIGYA_ACCOUNT_INFO, event.detail);
						}
					}
				});

				window.initGigyaSDK && window.initGigyaSDK();

			} else {
				// New Gigya integration approach
				// eslint-disable-next-line no-undef
				gigyaSdkHelper.loadGigyaSDK(process.env.VUE_APP_GIGYA_APP_KEY).catch(err => {
					// eslint-disable-next-line no-console
					console.log("Error loading Gigya SDK");
					return Promise.reject(err);
				})
					.then(() =>
						myStore.dispatch(actions.INIT_GIGYA_SESSION)
							.catch(err => {
								// Remove loyaltyData
								myStore.commit(mutationTypesTid6.RESET_LOYALTY_DATA);
								return Promise.reject(err);
							})
					)
					.then(() => {
						if (["/shop-selection"].includes(window.location.pathname)) {
							myStore.dispatch(mutationTypesTid6.GET_LOYALTY_OFFERS)
								.catch(err => {
									myStore.commit(mutationTypesTid6.RESET_LOYALTY_DATA);
									return Promise.reject(err);
								});
						}
					}
					);
			}
		}

		if (window.FORCE_WHITE_SCREEN === true) {
			sessionStorage.removeItem("gboData");
			sessionStorage.removeItem("loyaltyData");
			throw new Error("Force white screen error initiated");
		} else {
			new Vue({
				el: "#app",
				router: router.default,
				i18n: i18nProvider,
				store: myStore,
				template: "<App/>",
				components: {App}
			});

			if (sessionStorage.getItem("gboData")) {
				const gboData = JSON.parse(sessionStorage.getItem("gboData"));
				sessionStorage.removeItem("gboData");

				if (gboData.logout) {
					localStorage.removeItem("admin-user");
					myStore.commit(ADMIN_REMOVE_JWT_TOKEN);
					return;
				}

				if (gboData.jwtToken) {
					window.localStorage.setItem("tenant", gboData.tenantId + "");
					myStore.dispatch("cacheJwtToken", {accessToken: gboData.jwtToken});
				}
			}

			if (sessionStorage.getItem("loyaltyData")) {
				const loyaltyData = JSON.parse(sessionStorage.getItem("loyaltyData"));
				sessionStorage.removeItem("loyaltyData");
				window.localStorage.setItem("tenant", loyaltyData.tenantId + "");
				myStore.commit(mutationTypes.UNSET_GLOBAL_SELECTED_SHOP);
				myStore.commit(actionTypes.REMOVE_USER_IDP_TOKEN);
				myStore.dispatch(actionTypes.REMOVE_USER_DATA);

				if (loyaltyData.userData && loyaltyData.idpToken) {
					myStore.dispatch(mutationTypes.PERSON_DATA_SET, loyaltyData.userData);
					myStore.commit(actionTypes.SET_USER_IDP_TOKEN, loyaltyData.idpToken);
				}
			}
		}
	};

	preInitApp()
		.then(() => Promise.all([createStore(), import("./router")]))
		.then(([myStore, router]) => initialize(myStore, router));
};

if (appConfig.mobile.isMobileApp()) { // if this is mobile app wait for cordova to finish loading
	document.addEventListener("deviceready", initApp, false);
	document.addEventListener("deviceready", mobileEvents.onDeviceReady, false);
	document.addEventListener("deviceready", initAdjustTracking, false);
	document.addEventListener("deviceready", initFBSDK, false);
	document.addEventListener("deviceready", loadFacebookTracking, false);
} else {
	mobileEvents.onDeviceReady();
	if (!(typeof window !== "undefined" && window.self && window.self.name === "iframe_gbo")) {
		initApp();
	}
}

function loadFacebookTracking() {
	if (appConfig.isBM100()) return;
	const mobileApi = appConfig.mobile.mobileApi();
	const platform = mobileApi.platformId;
	if (platform === "ios") {
		const permissionAsked = window.localStorage.getItem("iOSTrackingPermissionAsked");
		if (!permissionAsked) {
			askTrackingPermission();
		} else {
			initFacebookTracking();
		}
	} else {
		initFacebookTracking();
	}
}

function initFacebookTracking() {
	window.loadThirdPartyScript(appConfig.tenantId, "mobile", window.initiateFacebookSDK, "FACEBOOK_SDK");
	window.loadThirdPartyScript(appConfig.tenantId, "mobile", window.loadFacebookPixel, "FACEBOOK_PIXEL");
}

async function initFBSDK() {
	if(!appConfig.isBM100()) return;

	// should be moved when more tenant are introduced with FB SDK
	const fbSdkAppParams = {
		appId: "6693720194076304",
		autoLogAppEvents: true
	};

	// use hook after platform dom ready
	await FacebookLogin.initialize({
		appId: fbSdkAppParams.appId,
		autoLogAppEvents: fbSdkAppParams.autoLogAppEvents
	});

	// eslint-disable-next-line
	console.log("[FB SDK Initialized]");
}

function initAdjustTracking() {
	const useAdjustTrackingIntegration = appConfig.isBM6();
	console.log("AdjustTrackingIntegration:", useAdjustTrackingIntegration); // eslint-disable-line no-console

	if (!useAdjustTrackingIntegration) return;

	const environment = appConfig.isNonProdZone() ? AdjustEnvironment.Sandbox : AdjustEnvironment.Production;

	console.log("AdjustTrackingIntegration env:", environment); // eslint-disable-line no-console
	const appToken = "1ubqzn9sjyww";
	const adjustConfig = new AdjustConfig(appToken, environment);
	Adjust.create(adjustConfig);
}

function askTrackingPermission() {
	AdvertisingId.requestTracking()
		.then((response) => {
			if (response.value === "Authorized") {
			// eslint-disable-next-line no-console
				console.log("[INFO] User allowed Facebook tracking");
				initFacebookTracking();
			}
		})
		.catch((err) => {
			// eslint-disable-next-line no-console
			console.log("[INFO] User declined Facebook tracking", err.message);
		});
	// you can only ask once, so after the first time read info instead of asking
	window.localStorage.setItem("iOSTrackingPermissionAsked", true);
}

if (appConfig.getBusinessModelId() === appConfig.businessModelsEnum.bm100.id) {
	document.querySelector("[name='apple-mobile-web-app-capable']").remove();
}

window.addEventListener("message", function(event) {
	const gboDomains = appConfig.gboAppUrl();
	const loyaltyDomains = appConfig.loyaltyAppUrl();

	if (!gboDomains || !loyaltyDomains) {
		return;
	}

	const originDomain = getBaseDomain(event.origin);

	if (isDomainMatch(gboDomains, originDomain)) {
		gboIframe();
		return;
	}

	if (isDomainMatch(loyaltyDomains, originDomain)) {
		loyaltyIframe();
	}
}, false);


function gboIframe () {
	sessionStorage.setItem("gboData", JSON.stringify(event.data));
	if (event.data && (event.data.jwtToken || event.data.logout)) {
		const style = document.createElement("style");
		style.textContent = `.top-menu-admin {
          display: none !important;
        }`;
		document.head.appendChild(style);
		initApp();
	}
}

function loyaltyIframe () {
	if (event.data) {
		sessionStorage.setItem("loyaltyData", JSON.stringify(event.data));
		const style = document.createElement("style");
		style.textContent = `.logo-link, .profile-icon, .developer-mode, .login, #cookie-information-template-wrapper {
          display: none !important;
        }`;
		document.head.appendChild(style);
		initApp();
	}
}
